import { SyntheticEvent, useEffect, useRef, useState } from 'react'

import useVideojsPlayer from '~/components/VideoPlayer/useVideojsPlayer'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { getShowUserFeedbackModal } from '~/user-feedback/redux/user-feedback'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import BootcampVideoProgressReward from '~/components/BootcampVideoProgressReward'
import {
  setEndedActionCreater,
  setPlayingActionCreater,
  setProgressPercentageActionCreater,
  setResetActionCreater,
  setVideoLengthActionCreater,
} from '~/bootcamp/slices/bootcamp-video.slice'
import getVideoXP from '~/bootcamp/utils/getVideoXP.utils'

import styles from './VideoPlayer.module.css'
import { RESTART_POSITION_SECONDS } from './VideoPlayer.const'
import { VideoPlayerProps, VideoPlayerState } from './VideoPlayer.types'

const VideoPlayer = (props: VideoPlayerProps): React.ReactElement => {
  const showUserFeedbackModal = useAppSelector(getShowUserFeedbackModal)
  const [playTime, setPlayTime] = useState(0)
  const [videoLength, setVideoLength] = useState(1)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const progressPercentage = Math.min(Math.ceil((playTime / videoLength) * 100), 100)
  const xpReward = getVideoXP(videoLength)
  const dispatch = useAppDispatch()
  const {
    options: { sources },
    videoRef,
    wrapperClassName = '',
    isCurriculumVideo = false,
    onProgress,
    onRestart,
    onSeek,
  } = props

  // Whether *this* player finished. The `ended` flag in the bootcampVideo slice is shared by every player on the
  // page — an activity modal's hint video ending would otherwise reset the level video's watch progress.
  const hasEndedRef = useRef(false)
  // The player is created once, so these are read through refs rather than captured in its event handlers.
  const onProgressRef = useRef(onProgress)
  const onRestartRef = useRef(onRestart)
  onProgressRef.current = onProgress
  onRestartRef.current = onRestart

  useEffect(() => {
    // videoLength is 1 until loadedmetadata lands, so an early tick would otherwise read as a full watch.
    if (videoLength > 1) onProgressRef.current?.(playTime, videoLength)
  }, [playTime, videoLength])

  const onPlay = (event: SyntheticEvent<HTMLVideoElement, Event>): void => {
    dispatch(setPlayingActionCreater(event.type === VideoPlayerState.PLAYING))
    if (hasEndedRef.current && event.type === VideoPlayerState.PLAYING) {
      hasEndedRef.current = false
      dispatch(setEndedActionCreater(false))
      setPlayTime(0)
      dispatch(setResetActionCreater(true))
      onRestartRef.current?.()
    }
  }

  const handleSeek = (): void => {
    if ((videoRef.current?.currentTime ?? 0) <= RESTART_POSITION_SECONDS) onRestartRef.current?.()
    onSeek?.()
  }

  const onEnded = (event: SyntheticEvent<HTMLVideoElement, Event>): void => {
    const isEndingEvent = event.type === VideoPlayerState.ENDED
    if (isEndingEvent) hasEndedRef.current = true
    dispatch(setEndedActionCreater(isEndingEvent))
    if (isEndingEvent) {
      dispatch(setResetActionCreater(false))
    }
  }

  useVideojsPlayer({ ...props, onSeek: handleSeek, setPlayTime, setVideoLength })

  useEffect(() => {
    if (videoLength > 1 && (isCurriculumVideo || !isMobile)) {
      dispatch(setVideoLengthActionCreater(videoLength))
    }
  }, [dispatch, isCurriculumVideo, isMobile, videoLength])

  useEffect(() => {
    if (showUserFeedbackModal && videoRef.current !== null) videoRef.current.pause()
  }, [showUserFeedbackModal, videoRef])

  useEffect(() => {
    if (!isCurriculumVideo && !isMobile) {
      dispatch(setProgressPercentageActionCreater(progressPercentage))
    }
  }, [dispatch, isMobile, progressPercentage, isCurriculumVideo])

  return (
    <>
      {isMobile &&
        !isCurriculumVideo && ( // The isMobile condition is feiling during the inital renders so I added the hidden class to prevent a blinking effect.
          <div className="md:hidden">
            <BootcampVideoProgressReward percent={progressPercentage} xp={xpReward} />
          </div>
        )}
      <div className={`rounded md:rounded-3xl overflow-hidden ${wrapperClassName}`}>
        <div data-vjs-player style={{ ['--video-theme-color' as any]: '#222' }}>
          <video
            className={`video-js ${styles['video-js']}`}
            id="vid1"
            onEnded={onEnded}
            onPause={onPlay}
            onPlay={onPlay}
            ref={videoRef}
          >
            <source
              src={sources && sources.length ? sources[0].src : ''}
              type={sources && sources.length ? sources[0].type : 'video/mp4'}
            />
          </video>
        </div>
      </div>
    </>
  )
}

export default VideoPlayer
